/* eslint-disable react/jsx-props-no-spreading */
import React, { useState } from 'react';
import {
  useForm,
  useFetch,
  useApiClient,
  getFormProps,
  OutlinedIcons,
} from '@marageti/z4-lib';
import { PasswordConfig } from '@marageti/z4-sdk/lib/auth';
import {
  Box,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { PasswordFormProps } from '../../pages/reset/set-password/types';
import FieldError, { errorOutlinedInputSx } from '../../pages/login/field-error';
import { solidButtonSx } from '../../pages/login/styles';

const { OutlinedEyeIcon, OutlinedEyeOffIcon } = OutlinedIcons;

const useValidationSchema = () => {
  const apiClient = useApiClient();
  const { data: passwordConfig } = useFetch<PasswordConfig>(apiClient.authClient.getPasswordConfig, [], true);

  const schema = z.object({
    password: z.string().min(1, 'Please enter a new password')
      .refine(async (value) => {
        if (!passwordConfig) return false;
        const regex = new RegExp(passwordConfig.filter);
        return regex.test(value);
      }, {
        message: passwordConfig?.description,
      }),
    confirmPassword: z.string(),
  }).refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match.",
    path: ['confirmPassword'],
  });

  return schema;
};

const PasswordForm = ({
  email,
  onSubmit,
}: PasswordFormProps) => {
  const schema = useValidationSchema();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(schema),
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  });

  const passwordValue = watch('password');
  const canSubmit = passwordValue.length > 0 && !isSubmitting;
  const passwordError = errors.password?.message;
  const confirmPasswordError = errors.confirmPassword?.message;

  return (
    <Box
      component="form"
      name="reset-form"
      {...getFormProps(handleSubmit(onSubmit))}
      sx={{
        flex: 1,
        minHeight: 0,
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Stack spacing={3} width="100%">
        {/* hidden username/email field for accessibility */}
        <TextField
          autoComplete="username"
          disabled
          hidden
          name="email"
          style={{ display: 'none' }}
          value={email}
        />
        <Stack spacing={1} width="100%">
          <TextField
            autoComplete="new-password"
            id="password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    onClick={() => setShowPassword((prev) => !prev)}
                    onMouseDown={(e) => e.preventDefault()}
                    edge="end"
                  >
                    {showPassword
                      ? <OutlinedEyeOffIcon color="primary" />
                      : <OutlinedEyeIcon color="primary" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            label="New Password"
            type={showPassword ? 'text' : 'password'}
            error={!!passwordError}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 0,
                ...(passwordError && errorOutlinedInputSx),
              },
            }}
            {...register('password')}
          />
          {passwordError && (
            <FieldError message={String(passwordError)} />
          )}
        </Stack>

        <Stack spacing={1} width="100%">
          <TextField
            autoComplete="new-password"
            id="confirmPassword"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                    onClick={() => setShowConfirmPassword((prev) => !prev)}
                    onMouseDown={(e) => e.preventDefault()}
                    edge="end"
                  >
                    {showConfirmPassword
                      ? <OutlinedEyeOffIcon color="primary" />
                      : <OutlinedEyeIcon color="primary" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            label="Confirm New Password"
            type={showConfirmPassword ? 'text' : 'password'}
            error={!!confirmPasswordError}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 0,
                ...(confirmPasswordError && errorOutlinedInputSx),
              },
            }}
            {...register('confirmPassword')}
          />
          {confirmPasswordError && (
            <FieldError message={String(confirmPasswordError)} />
          )}
        </Stack>
      </Stack>

      <Box sx={{ mt: 'auto', pt: 4 }}>
        <LoadingButton
          type="submit"
          variant="contained"
          fullWidth
          loading={isSubmitting}
          disabled={!canSubmit}
          sx={solidButtonSx}
        >
          Set Password
        </LoadingButton>
      </Box>
    </Box>
  );
};

export default PasswordForm;
